import { z } from "zod";
import {
  ColorOptionLabel,
  MiBox,
  MiDetailFields,
} from "@miplatform/components";
import {
  CASH_ON_DELIVERY_PAYMENT_ALLOWED,
  CONFIGURABLE_COLORS,
  INPUT_TYPES,
  INVOICE_PAYMENT_ALLOWED,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { capitalize, simpleSort } from "@miplatform/utils";
import { mdiFormatListBulleted } from "@mdi/js";
import { Footer } from "@mibuildweb/components";

export function Details({
  workOrderTemplate = {},
  trades = [],
  onUpdate = () => null,
}) {
  const paymentMethods = [];
  if (workOrderTemplate.codPaymentAllowed) {
    paymentMethods.push(CASH_ON_DELIVERY_PAYMENT_ALLOWED);
  }
  if (workOrderTemplate.invoicePaymentAllowed) {
    paymentMethods.push(INVOICE_PAYMENT_ALLOWED);
  }
  paymentMethods.sort((a, b) => simpleSort(a, b));

  const edit = useEdit({
    ...workOrderTemplate,
    module: workOrderTemplate.module || null,
    paymentMethods,
  });

  const detailFields = [
    {
      label: "Name",
      name: "name",
      beforeOnChange: capitalize,
      width: "33%",
      validationRule: z.string().min(1, "Order Type Name is required"),
    },
    {
      label: "Trade",
      name: "tradeId",
      fieldType: INPUT_TYPES.SELECT,
      options: trades,
      width: "33%",
      validationRule: z.string().min(1, "Trade Name is required"),
    },
    {
      label: "Abbr.",
      name: "shortName",
      beforeOnChange: (value) => value.toUpperCase(),
      width: "33%",
      validationRule: z.string().min(1, "Abbreviation is required"),
    },
    {
      label: "Color",
      name: "color",
      fieldType: INPUT_TYPES.SELECT,
      options: CONFIGURABLE_COLORS.map(({ color, label }) => ({
        label: <ColorOptionLabel label={label} color={color} />,
        value: color,
      })),
      width: "33%",
      validationRule: z.string().min(1, "Color is required"),
    },
    {
      label: "Follow-Up Default Offset Hours",
      name: "followUpDefaultOffsetHours",
      width: "33%",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .optional()
        .refine((val) => !val || /^[0-9]{1,2}$/.test(val), {
          message: "Must be a number with at most two digits",
        })
        .or(z.literal("")),
    },
    {
      label: "Follow-Up Max Offset Hours",
      name: "followUpMaxOffsetHours",
      width: "33%",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .optional()
        .refine((val) => !val || /^[0-9]{1,2}$/.test(val), {
          message: "Must be a number with at most two digits",
        })
        .or(z.literal("")),
    },
    {
      label: "Max Times Put On Hold",
      name: "maxTimesPutOnHold",
      width: "33%",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .optional()
        .refine((val) => !val || val >= 0, {
          message: "Must be a number greater than or equal to 0",
        })
        .or(z.literal("")),
    },
  ];

  edit.setValidation([...detailFields]);
  const onCancel = () => {
    edit.reset();
  };

  return (
    <>
      <MiBox>
        <MiDetailFields
          headerProps={{
            icon: mdiFormatListBulleted,
            title: "Detail Fields",
          }}
          detailFields={detailFields}
          edit={edit}
        />
      </MiBox>
      <Footer
        show={edit.isDirty()}
        handleCancelClick={onCancel}
        handleSaveClick={() => onUpdate(edit.edits)}
        disableSave={!edit.isValid()}
      />
    </>
  );
}
